/**
 * Screen 13 — "Is it working". Funnel, outcome split, deflection, install
 * health, recent leads, CSV export, "Share your widget page"
 * (./WidgetPageShare.tsx), "Share your stats" links (./ShareLinks.tsx)
 * and a next-steps checklist.
 *
 * Every lead count here is LIVE leads only (the server filters `mode = 'live'`
 * in every dashboard statement), so test runs never inflate it.
 *
 * Chart choices (dataviz method): the funnel is one series → horizontal bars
 * in one colour, no legend, each bar labelled with its value and step
 * conversion (the labels ARE the table view). The outcome split is a
 * part-to-whole of four categories → one stacked bar, 2 px surface gaps, a
 * legend that carries the counts, colours from the validated --viz-1..4 slots.
 */
import { useEffect, useState } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { UNMATCHED_REASONS, type Funnel } from '../api/types';
import { UNMATCHED_COPY } from '../leads/unmatched';
import { AgentChangesBanner } from '../agent/AgentChangesBanner';
import { Link } from '../router';
import { useProgress } from '../setup/progress';
import { useSites } from '../state/sites';
import { useLastSeen } from '../state/lastSeen';
import { Empty, ErrorNotice, Loading, PageHead, Segmented, Time, humanize, useToast } from '../ui/kit';
import { ShareLinks } from './ShareLinks';
import { WidgetPageShare } from './WidgetPageShare';
import { WidgetUsageLimits } from './WidgetUsageLimits';
import { useLocalState, useMutation, useQuery } from '../ui/hooks';

type Range = '7' | '30' | '90';

function periodFor(days: Range) {
  const to = new Date();
  const from = new Date(to.getTime() - Number(days) * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * A rate or a count the server may leave out (an older server, a partial
 * response): anything that is not a finite number shows as "—", never
 * "NaN%" or "NaN".
 */
const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
export const pct = (n: number | null | undefined) => (finite(n) ? `${Math.round(n * 1000) / 10}%` : '—');
const count = (n: number | null | undefined) => (finite(n) ? n : 0);
const nfBase = new Intl.NumberFormat('en-US');
const nf = { format: (n: number | null | undefined) => (finite(n) ? nfBase.format(n) : '—') };

export function FunnelBars({ f }: { f: Omit<Funnel, 'filters'> }) {
  const steps: Array<[string, number]> = [
    ['Impressions', f.impressions],
    ['Leads captured', f.captures],
    ['With diagnosis', f.diagnoses],
    // Reached YOU (inbox or webhook) is not reached a CONTRACTOR: an
    // unmatched lead is the first and not the second.
    ['Delivered to you', f.delivered],
    ...(typeof f.offered === 'number' ? ([['Offered to a contractor', f.offered]] as Array<[string, number]>) : []),
    ['Accepted', f.accepted],
    ['Connected', f.connected],
  ];
  const max = Math.max(1, ...steps.map(([, v]) => count(v)));
  // Server migration 094: a branch off "unmatched", not a step after
  // "Connected", so it is shown after the steps with no conversion rate.
  const network = typeof f.sent_to_network === 'number' ? f.sent_to_network : null;
  return (
    <div className="funnel" role="list" aria-label="Funnel">
      {steps.map(([label, v], i) => {
        const prev = i > 0 ? steps[i - 1]![1] : null;
        const conv = prev && finite(v) ? `${Math.round((v / prev) * 100)}%` : '';
        return (
          <div className="fr" role="listitem" key={label} title={`${label}: ${nf.format(v)}${conv ? ` (${conv} of previous step)` : ''}`}>
            <span>{label}</span>
            <span className="track" aria-hidden="true">
              <span className="fill" style={{ display: 'block', width: `${(count(v) / max) * 100}%` }} />
            </span>
            <span className="num" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ink)' }}>
              {nf.format(v)}
            </span>
            <span className="conv">{conv ? `${conv} ↓` : ''}</span>
          </div>
        );
      })}
      {network !== null ? (
        <div className="fr" role="listitem" title={`Sent to network: ${nf.format(network)} (unmatched leads the homeowner asked to be referred)`}>
          <span>Sent to network</span>
          <span className="track" aria-hidden="true">
            <span className="fill" style={{ display: 'block', width: `${(count(network) / max) * 100}%` }} />
          </span>
          <span className="num" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ink)' }}>
            {nf.format(network)}
          </span>
          <span className="conv" />
        </div>
      ) : null}
    </div>
  );
}

/**
 * `list`: the legend as one row per outcome with its share — the /share page's
 * Outcomes card sits beside the taller funnel, and a one-line legend left it
 * mostly empty (owner feedback 2026-09-27).
 */
export function OutcomeSplit({ f, list = false }: { f: Omit<Funnel, 'filters'>; list?: boolean }) {
  const parts = [
    { key: 'diy_resolved', label: 'Fixed it themselves', v: count(f.outcomes?.diy_resolved), color: 'var(--viz-1)' },
    { key: 'parts', label: 'Needed a part', v: count(f.outcomes?.parts), color: 'var(--viz-2)' },
    // `requested_action` (the homeowner's choice of CTA), not a close outcome.
    { key: 'sent_to_team', label: 'Asked for your team', v: count(f.outcomes?.sent_to_team), color: 'var(--viz-3)' },
    { key: 'escalated', label: 'Escalated', v: count(f.outcomes?.escalated), color: 'var(--viz-4)' },
  ];
  const total = parts.reduce((a, p) => a + p.v, 0);
  if (!total) return <p className="small muted">No outcomes recorded in this period yet.</p>;
  return (
    <div className="stack-sm">
      <div className="split" role="img" aria-label={parts.map((p) => `${p.label} ${p.v}`).join(', ')}>
        {parts
          .filter((p) => p.v > 0)
          .map((p) => (
            <span key={p.key} style={{ width: `${(p.v / total) * 100}%`, background: p.color }} title={`${p.label}: ${p.v} (${Math.round((p.v / total) * 100)}%)`} />
          ))}
      </div>
      {list ? (
        <ul className="outcome-list">
          {parts.map((p) => (
            <li key={p.key}>
              <span>
                <i style={{ background: p.color }} aria-hidden="true" />
                {p.label}
              </span>
              <strong className="num">{nf.format(p.v)}</strong>
              <span className="num muted">{Math.round((p.v / total) * 100)}%</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="legend">
          {parts.map((p) => (
            <span key={p.key}>
              <i style={{ background: p.color }} />
              {p.label} <strong className="num" style={{ color: 'var(--ink)' }}>{p.v}</strong>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Leads no contractor got, by reason (migration 077). Shown whenever there are
 * any, so an unmatched lead never hides inside "Delivered". `links` points
 * each reason at its fix (the signed-in dashboard; a share link has none).
 */
export function UnmatchedSummary({ f, links = false, fixes = true }: { f: Omit<Funnel, 'filters'>; links?: boolean; fixes?: boolean }) {
  const total = count(f.unmatched);
  if (!total) return null;
  const reasons = UNMATCHED_REASONS.map((r) => ({ r, n: count(f.unmatched_reasons?.[r]) })).filter((x) => x.n > 0);
  const known = reasons.reduce((a, x) => a + x.n, 0);
  return (
    <section className="panel" aria-labelledby="unmatched-head">
      <div className="panel-head">
        <h2 id="unmatched-head">Unmatched</h2>
        <span className="small muted">
          {nf.format(total)} {total === 1 ? 'lead' : 'leads'} no contractor got
        </span>
      </div>
      <div className="panel-body">
        <ul className="stack-sm" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {reasons.map(({ r, n }) => (
            <li key={r} className="row-between small unmatched-row">
              <span>
                <strong style={{ color: 'var(--ink)' }}>{UNMATCHED_COPY[r].short}</strong>
                {/* The fix is advice to the partner; a shared stats page shows the reason only. */}
                {fixes ? <> · {UNMATCHED_COPY[r].fix}</> : null}{' '}
                {links ? <Link to={UNMATCHED_COPY[r].to}>Fix it</Link> : null}
              </span>
              <span className="num">{nf.format(n)}</span>
            </li>
          ))}
          {total > known ? (
            <li className="row-between small unmatched-row">
              <span className="muted">Reason not recorded (before 2026-09-27)</span>
              <span className="num">{nf.format(total - known)}</span>
            </li>
          ) : null}
          {count(f.sent_to_network) > 0 ? (
            <li className="row-between small unmatched-row">
              <span>
                <strong style={{ color: 'var(--ink)' }}>Sent to our matching network (eLocal)</strong> · the homeowner asked to be referred
                {count(f.network_accepted) > 0 ? <> · {nf.format(count(f.network_accepted))} taken</> : null}
              </span>
              <span className="num">{nf.format(count(f.sent_to_network))}</span>
            </li>
          ) : null}
        </ul>
      </div>
    </section>
  );
}

export function Dashboard() {
  const { api, role, org } = useSession();
  const { current, pairs } = useSites();
  const { isDone, markDone, remaining, nextPath, install } = useProgress();
  // A page from us and no website of their own (the installer step's answer,
  // InstallOwner.tsx): "Install health" would only ever say "Not installed".
  const [installer] = useLocalState<string>(`fx-installer-${org?.id}`, 'self');
  const pageOnly = installer === 'none' && !install?.installed;
  const toast = useToast();
  const lastSeen = useLastSeen();
  const [range, setRange] = useState<Range>('30');
  // '' is every site. The picker shows only for an org with more than one
  // site; the export and the other panels stay organisation-wide.
  const [funnelSite, setFunnelSite] = useState('');
  const siteFilter = pairs.some((p) => p.live.site_key === funnelSite) ? funnelSite : '';
  const period = periodFor(range);
  const funnel = useQuery(
    () => api.funnel({ ...period, ...(siteFilter ? { site_key: siteFilter } : {}) }),
    [range, siteFilter],
    { enabled: can(role, 'funnel') },
  );
  const health = useQuery(() => api.installHealth(current?.live.site_key), [current?.live.site_key], { enabled: can(role, 'installHealth') });
  const recent = useQuery(() => api.listLeads({ limit: '6', mode: 'live' }), [], { enabled: can(role, 'leads') });
  // "Test a lead" is done once a test lead exists (TestLead.tsx), wherever it
  // was filed — another browser, or a partner who never pressed the step's
  // Continue. Asked only while the checklist still lists the step.
  const testPending = can(role, 'leads') && !isDone('test');
  const testLead = useQuery(() => api.listLeads({ limit: '1', mode: 'test' }), [], { enabled: testPending });
  const hasTestLead = Boolean(testLead.data?.items.length);
  useEffect(() => {
    if (hasTestLead) markDone('test');
  }, [hasTestLead, markDone]);
  const exporter = useMutation(async () => {
    const { job_id } = await api.startExport(period.from, period.to);
    const status = await api.exportStatus(job_id);
    if (status.status !== 'ready' || !status.download_url) throw new Error('Export not ready');
    const blob = await api.downloadExport(status.download_url);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finderlyfix-leads-${range}d.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    return status;
  });
  const f = funnel.data;
  // Export and sharing are for when there is something to export or share:
  // with no live lead yet neither is shown (a Share button that opened an
  // empty export and a link to an empty dashboard read as broken; second
  // portal audit, 2026-09-27). Undecided until both reads answer, so the
  // page does not flash one layout then the other.
  const shareable = can(role, 'exportCsv') || can(role, 'shareDashboard');
  const dataKnown = (!can(role, 'leads') || !recent.loading) && (!can(role, 'funnel') || !funnel.loading);
  const hasData = Boolean(recent.data?.items.length) || (f?.captures ?? 0) > 0;
  const collapsed = shareable && dataKnown && !hasData;
  const exportButton = can(role, 'exportCsv') ? (
    <button
      type="button"
      className="btn"
      disabled={exporter.pending}
      onClick={async () => {
        const s = await exporter.run();
        if (s) toast(`Exported ${s.rows ?? 0} leads${s.truncated ? ' (capped at 5,000)' : ''}`);
      }}
    >
      {exporter.pending ? 'Exporting…' : 'Export CSV'}
    </button>
  ) : null;
  // Required steps only, from real data (setup/status.ts); optional ones never count.
  const todo = remaining;

  return (
    <div>
      {/* Migration 096: what AI agents changed (Undo) or wait on (Approve / Reject), first thing on the page. */}
      <AgentChangesBanner />
      <PageHead
        title="Dashboard"
        lede={`${org?.display_name ?? ''} · live leads only; test runs are excluded.`}
        actions={
          <>
            {pairs.length > 1 && can(role, 'funnel') ? (
              <label className="site-switch" htmlFor="dashboard-site">
                <span className="label">Results for</span>
                <select
                  id="dashboard-site"
                  className="input"
                  style={{ minHeight: 32, padding: '3px 8px' }}
                  value={siteFilter}
                  onChange={(e) => setFunnelSite(e.target.value)}
                >
                  <option value="">All sites</option>
                  {pairs.map((p) => (
                    <option key={p.live.site_key} value={p.live.site_key}>
                      {p.live.label || p.live.site_key}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <Segmented
              label="Period"
              value={range}
              onChange={setRange}
              options={[
                { value: '7', label: '7 days' },
                { value: '30', label: '30 days' },
                { value: '90', label: '90 days' },
              ]}
            />
            {dataKnown && !collapsed ? exportButton : null}
          </>
        }
      />
      <div className="stack-lg">
        <ErrorNotice error={exporter.error ?? funnel.error} />
        {todo.length ? (
          <section className="panel panel-pad row-between">
            <div className="stack-sm" style={{ gap: 2 }}>
              <strong style={{ color: 'var(--ink)' }}>Next: {todo[0]!.title}</strong>
              <span className="small muted">
                {todo.length} setup {todo.length === 1 ? 'step' : 'steps'} left · {todo.map((s) => s.title).join(' · ')}
              </span>
            </div>
            <Link className="btn btn-primary" to={nextPath ?? `/setup/${todo[0]!.slug}`}>
              Continue setup
            </Link>
          </section>
        ) : null}

        {can(role, 'funnel') ? (
          funnel.loading && !f ? (
            <Loading />
          ) : f ? (
            <>
              <div className="metrics">
                <div className="metric">
                  <div className="v">{nf.format(f.impressions)}</div>
                  <div className="k">Widget impressions</div>
                </div>
                <div className="metric">
                  <div className="v">{nf.format(f.captures)}</div>
                  <div className="k">Leads captured</div>
                </div>
                <div className="metric">
                  <div className="v">{pct(f.deflection_rate)}</div>
                  <div className="k">Deflection rate</div>
                </div>
                <div className="metric">
                  <div className="v">{pct(f.false_deflection_rate)}</div>
                  <div className="k">False deflection</div>
                </div>
              </div>
              <div className="grid-2">
                <section className="panel">
                  <div className="panel-head">
                    <h2>Funnel</h2>
                    <span className="small muted">
                      last {range} days
                      {siteFilter ? ` · ${pairs.find((p) => p.live.site_key === siteFilter)?.live.label ?? siteFilter}` : ''}
                    </span>
                  </div>
                  <div className="panel-body">
                    <FunnelBars f={f} />
                  </div>
                </section>
                <section className="panel">
                  <div className="panel-head">
                    <h2>Outcomes</h2>
                  </div>
                  <div className="panel-body stack">
                    <OutcomeSplit f={f} />
                    <p className="small muted">
                      Deflection counts leads closed as an avoided dispatch or a deflected job; false deflection is the share of those
                      that reopened.
                    </p>
                  </div>
                </section>
              </div>
              <UnmatchedSummary f={f} links />
            </>
          ) : null
        ) : null}

        {can(role, 'usageLimits') && current?.live.site_key ? <WidgetUsageLimits siteKey={current.live.site_key} /> : null}

        <div className="grid-2">
          {can(role, 'installHealth') && !pageOnly ? (
            <section className="panel">
              <div className="panel-head">
                <h2>Install health</h2>
                <Link to="/setup/verify" className="btn btn-sm btn-ghost panel-link">
                  Verify
                </Link>
              </div>
              {/* The same "installed" as the rail and the Verify page (setup/status.ts installState). */}
              {install ? (
                <p className="panel-body small install-summary" style={{ margin: 0 }}>
                  {install.installed ? (
                    <>
                      <span className="dotstat ok">Installed ✓</span>{' '}
                      <span className="muted">{install.live ? '· the widget is loading on your site' : '· a page check found it'}</span>
                    </>
                  ) : (
                    <>
                      <span className="dotstat warn">Not installed yet</span> <span className="muted">· no impressions and no passing page check</span>
                    </>
                  )}
                </p>
              ) : null}
              {health.loading ? (
                <Loading />
              ) : health.error ? (
                <div className="panel-body">
                  <ErrorNotice error={health.error} />
                </div>
              ) : health.data?.origins.length ? (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th scope="col">Website</th>
                        <th scope="col">Status</th>
                        <th scope="col">Last impression</th>
                      </tr>
                    </thead>
                    <tbody>
                      {health.data.origins.map((o) => (
                        <tr key={o.origin}>
                          <td className="mono small">{o.origin}</td>
                          <td>
                            <span className={`dotstat ${o.status === 'installed' ? 'ok' : o.status === 'error' ? 'bad' : 'warn'}`}>
                              {o.status === 'installed' ? 'Installed' : o.status === 'error' ? (o.registered ? 'Problem' : 'Not allowed') : 'Not seen yet'}
                            </span>
                          </td>
                          <td>
                            <Time iso={lastSeen.origin(o.site_key, o.origin, o.last_impression_at)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Empty title="No origins yet" />
              )}
            </section>
          ) : null}
          {can(role, 'leads') ? (
            <section className="panel">
              <div className="panel-head">
                <h2>Recent leads</h2>
                <Link to="/leads" className="btn btn-sm btn-ghost panel-link">
                  All leads
                </Link>
              </div>
              {recent.loading ? (
                <Loading />
              ) : recent.error ? (
                <div className="panel-body">
                  <ErrorNotice error={recent.error} />
                </div>
              ) : recent.data?.items.length ? (
                <div className="table-wrap">
                  <table className="data">
                    <tbody>
                      {recent.data.items.map((l) => (
                        <tr key={l.id}>
                          <td>
                            <Link className="rowlink" to={`/leads/${l.id}`}>
                              {l.problem_label ?? humanize(l.trade)}
                            </Link>
                          </td>
                          <td>
                            <span className="tag">{humanize(l.status)}</span>
                          </td>
                          <td className="num">
                            <Time iso={l.created_at} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Empty title="No live leads yet">Test leads show under Leads with a Test badge.</Empty>
              )}
            </section>
          ) : null}
        </div>

        {/* Two kinds of sharing, kept apart and named for what they share
            (owner feedback 2026-09-27): the widget, for homeowners; the numbers, privately. */}
        <WidgetPageShare />
        {!collapsed && dataKnown && can(role, 'shareDashboard') ? <ShareLinks /> : null}
      </div>
    </div>
  );
}
